import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { EnterpriseComplianceFiltersContext } from "../contexts/EcdFiltersContext";
import { Link } from "react-router-dom";
import { SortDirections, WafflesPaginationProps, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { EnterpriseComplianceDashboardContext } from "../contexts/EcdContext";
import { SharedEcdDrillInTableData, sharedEcdDrillInColumnConfig, sharedEcdDrillInCustomSort, sharedEcdDrillInPaginationProps, sharedEcdDrillInSlicedTableData } from "../flyouts/EcdDrillInConfig";
import { Accordion } from "@app/shared/Accordion";
import { SearchBox } from "@app/shared/SearchBox";

interface EcdSpecialtyByGapsAccordionProps {
  groupCode: string;
  specialtyId: number;
  specialtyName: string;
}

export const EcdSpecialtyByGapsAccordion: FC<EcdSpecialtyByGapsAccordionProps> = (props: EcdSpecialtyByGapsAccordionProps) => {
  const filtersContext = useContext(EnterpriseComplianceFiltersContext);
  const ecdContext = useContext(EnterpriseComplianceDashboardContext);
  const [filterString, setFilterString] = useState("");
  const [data, setData] = useState<SharedEcdDrillInTableData[]>([]);
  const take = useRef(100).current;
  const [skip, setSkip] = useState(0);

  useEffect(() => {
    setSkip(0);
  }, [filterString]);

  const onOpenAccordion = () => {
    if (data.length > 0) {
      return;
    }

    const filters = structuredClone(filtersContext.apiReadyFilters);
    filters.specialtyGroupIds = [props.specialtyId];
    ecdContext.getGapsBySpecialtyDrillInData(filters).then((response) => {
      const newData: SharedEcdDrillInTableData[] = response.map((project) => {
        const projectLink = `/projects/${project.customProjectId}/adequacy/specialty/${props.groupCode}?all=false&gaps=true`
        /* oxlint-disable sort-keys */
        return {
          [wafflesKeyColumnName]: project.customProjectId.toString(),
          projectName: <Link key={project.projectName} to={projectLink} target="_blank" rel="noopener noreferrer" className="qes-button qes-button-secondary">{project.projectName}</Link>,
          compositeFillableGaps: project.fillableGapCount.toLocaleString(),
          unfillableGaps: project.unfillableGapCount.toLocaleString(),
          overallGaps: project.gapCount.toLocaleString(),
        };
        /* oxlint-enable sort-keys */
      });
      customSort(newData, "compositeFillableGaps", SortDirections.Desc);
    });
  }

  const customSort = (records: SharedEcdDrillInTableData[], column: keyof SharedEcdDrillInTableData, direction: SortDirections) => {
    const newData = sharedEcdDrillInCustomSort(
      data.length > 0 ? data : records,
      column,
      direction,
      "compositeFillableGaps", SortDirections.Desc
    );
    setData(newData);
    return records;
  }

  const paginationProps: WafflesPaginationProps = useMemo(() => (
    sharedEcdDrillInPaginationProps(skip, take, setSkip, data, filterString)
  ), [data, skip, take, filterString]);

  const slicedData = useMemo(() => {
    return sharedEcdDrillInSlicedTableData(data, filterString, skip, take);
  }, [data, skip, take, filterString]);

  return (
    <Accordion
      key={props.specialtyName}
      title={props.specialtyName}
      onPanelWillOpen={onOpenAccordion}
      testId={`ecd-specialty-by-gaps-accordion__${props.specialtyName}-accordion`}
    >
      <SearchBox
        fieldsetClass="row mx-0"
        inputLabel="Filter on Project Name"
        onSubmit={(value) => setFilterString(value)}
        testId={`ecd-specialty-by-gaps-accordion__filter--${props.specialtyId}`}
      />
      <Waffles<SharedEcdDrillInTableData>
        columns={sharedEcdDrillInColumnConfig}
        customSort={customSort}
        data={slicedData}
        enablePagination={true}
        loading={data.length === 0 && filterString === ""}
        fullWidth={true}
        paginationProps={paginationProps}
        passThroughSorting={true}
        testId={`ecd-specialty-by-gaps-accordion__table--${props.specialtyId}`}
      />
    </Accordion>
  )
}